import {useState} from 'react';
import {SearchField, view} from '@sail/ui';
import {WalletSelectListEmpty} from 'src/v2/components/walletSelect/walletList/WalletSelectListEmpty';
import {WalletSelectListResults} from 'src/v2/components/walletSelect/walletList/WalletSelectListResults';
import {WalletSelectListSecondary} from 'src/v2/components/walletSelect/walletList/WalletSelectListSecondary';
import {Text} from 'src/v2/components/common';
import type {PaymentOption} from 'src/v2/state/types';
import {message, useIntl} from '@sail/intl';

const MAX_SEARCH_RESULTS = 8;

interface Props {
  walletOptions: PaymentOption[];
  defaultWalletOptions: PaymentOption[];
  onWalletPress: (walletOption: PaymentOption) => void;
  onDirectDepositPress?: () => void;
  onConnectedWalletPress?: () => void;
}

const messages = {
  selectWallet: message({
    id: 'crypto.payins.v2.wallet_select_list.title',
    description: 'Title for the wallet selection list',
    defaultMessage: 'Select wallet',
  }),
  searchPlaceholder: message({
    id: 'crypto.payins.v2.wallet_select_list.search_placeholder',
    description: 'Placeholder text for the search field in wallet select list',
    defaultMessage: 'Search through over 400 wallets',
  }),
};

export const WalletSelectList = ({
  walletOptions,
  defaultWalletOptions,
  onWalletPress,
  onDirectDepositPress,
  onConnectedWalletPress,
}: Props) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');

  const isSearching = searchQuery.length > 0;

  const searchResults = isSearching
    ? walletOptions
        .filter(({name}) =>
          name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
        .slice(0, MAX_SEARCH_RESULTS)
    : [];

  const displayedWallets = isSearching ? searchResults : defaultWalletOptions;
  const showSecondaryActions =
    !isSearching && (!!onDirectDepositPress || !!onConnectedWalletPress);

  return (
    <view.div css={{stack: 'y', gapY: 'space.150'}}>
      <Text type="bodyEmphasized">
        {intl.formatMessage(messages.selectWallet)}
      </Text>

      <SearchField
        label={intl.formatMessage(messages.selectWallet)}
        hiddenElements={['label']}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        onInputChange={setSearchQuery}
        menuTrigger="manual"
        size="large"
        css={{width: 'fill'}}
      />

      {displayedWallets.length > 0 ? (
        <>
          <WalletSelectListResults
            walletOptions={displayedWallets}
            onWalletPress={onWalletPress}
          />

          {showSecondaryActions ? (
            <WalletSelectListSecondary
              onDirectDepositPress={onDirectDepositPress}
              onConnectedWalletPress={onConnectedWalletPress}
            />
          ) : null}
        </>
      ) : (
        <view.div css={{paddingY: 'space.500'}}>
          <WalletSelectListEmpty onDirectDepositPress={onDirectDepositPress} />
        </view.div>
      )}
    </view.div>
  );
};
